import React, { useState, useRef, useEffect } from 'react';
import VariablePicker from '../components/variable_Picker';
import { useVariables } from '../contexts/VariableContext';
import { resolveVariables } from '../utils/variableResolver';

// Rich Text Input Component
const RichTextInput = ({ 
  generatedKey,
  placeholder = "Start typing...", 
  className = "" 
}) => {
  const { variableDict, updateVariable, updateTrigger } = useVariables();
  const [showPicker, setShowPicker] = useState(false);
  const [pickerPosition, setPickerPosition] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const editorRef = useRef(null);
  const lastTriggerRef = useRef(updateTrigger);

  // Listen for variable updates and refresh badges
  useEffect(() => {
    if (updateTrigger !== lastTriggerRef.current && editorRef.current) {
      lastTriggerRef.current = updateTrigger;
      
      // Update all variable badges to show current values
      const badges = editorRef.current.querySelectorAll('[data-variable]');
      badges.forEach(badge => {
        const varKey = badge.dataset.variable;
        if (variableDict[varKey]) {
          badge.textContent = String(variableDict[varKey].value || varKey);
        }
      });
      
      // Update stored value
      if (generatedKey && variableDict[generatedKey]) {
        const rawValue = getContent();
        if (variableDict[generatedKey].value !== rawValue) {
          updateVariable(generatedKey, rawValue);
        }
      }
    }
  }, [updateTrigger, variableDict]);

  const handleInput = (e) => {
    const text = e.target.textContent;
    
    // Store raw value with @variable references
    const rawValue = getContent();
    if (generatedKey && variableDict[generatedKey]) {
      updateVariable(generatedKey, rawValue);
    }
    
    // Check if @ is typed
    if (text.endsWith('@')) {
      setShowPicker(true);
      setSearchTerm('');
      
      const selection = window.getSelection();
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      
      setPickerPosition({
        top: rect.bottom + window.scrollY + 5,
        left: rect.left + window.scrollX
      });
    } else if (showPicker) {
      const atIndex = text.lastIndexOf('@');
      if (atIndex !== -1) {
        const term = text.substring(atIndex + 1);
        setSearchTerm(term);
      } else {
        setShowPicker(false);
      }
    }
  };

  const insertVariable = (varKey) => {
    const editor = editorRef.current;
    const selection = window.getSelection();
    const text = editor.textContent;
    const atIndex = text.lastIndexOf('@');
    
    editor.innerHTML = '';
    
    const beforeAt = text.substring(0, atIndex);
    
    if (beforeAt) {
      const textNode = document.createTextNode(beforeAt);
      editor.appendChild(textNode);
    }
    
    // Create variable badge
    const badge = document.createElement('span');
    badge.contentEditable = 'false';
    badge.className = `inline-flex items-center px-2 py-0.5 mx-0.5 rounded border ${variableDict[varKey]?.color || 'bg-gray-200'} text-sm font-medium`;
    badge.textContent = String(variableDict[varKey]?.value || varKey);
    badge.dataset.variable = varKey;
    badge.title = `@${varKey}`;
    editor.appendChild(badge);
    
    const spaceNode = document.createTextNode('\u00A0');
    editor.appendChild(spaceNode);
    
    const range = document.createRange();
    range.setStartAfter(spaceNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    
    setShowPicker(false);
    setSearchTerm('');
    editor.focus();
    
    // Update stored value
    const newRawValue = getContent();
    if (generatedKey) {
      updateVariable(generatedKey, newRawValue);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowPicker(false);
    }
    
    if (showPicker && ['ArrowUp', 'ArrowDown', 'Enter'].includes(e.key)) {
      return;
    }
    
    // Handle deleting badges with backspace
    if (e.key === 'Backspace') {
      const selection = window.getSelection();
      const range = selection.getRangeAt(0);
      
      if (range.collapsed) {
        const node = range.startContainer;
        const offset = range.startOffset;
        
        if (node.previousSibling && node.previousSibling.dataset && node.previousSibling.dataset.variable && offset === 0) {
          e.preventDefault();
          node.previousSibling.remove();
          
          const newRawValue = getContent();
          if (generatedKey) {
            updateVariable(generatedKey, newRawValue);
          }
        }
      }
    }
  };

  const clear = () => {
    if (editorRef.current) {
      editorRef.current.innerHTML = '';
      editorRef.current.focus();
      
      if (generatedKey) {
        updateVariable(generatedKey, '');
      }
    }
  };

  // Get value WITH variable references (@variable syntax)
  const getContent = () => {
    if (!editorRef.current) return '';
    
    let result = '';
    const children = editorRef.current.childNodes;
    
    children.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        result += node.textContent;
      } else if (node.dataset && node.dataset.variable) {
        result += `@${node.dataset.variable}`;
      }
    });
    
    return result;
  };

  // Get the RESOLVED value (with actual variable values substituted)
  const getActualContent = () => {
    const rawValue = getContent();
    return resolveVariables(rawValue, variableDict);
  };

  // Filter out current field's key from variables
  const getFilteredVariables = () => {
    const { [generatedKey]: _, ...filteredDict } = variableDict;
    return filteredDict;
  };

  return (
    <div className="relative">
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        className={`min-h-[200px] max-h-[400px] overflow-y-auto px-4 py-3 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors bg-white ${className}`}
        style={{ 
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word'
        }}
        suppressContentEditableWarning
      />
      
      {/* Placeholder */}
      {editorRef.current && editorRef.current.textContent === '' && (
        <div className="absolute top-3 left-4 text-slate-400 pointer-events-none font-mono">
          {placeholder}
        </div>
      )}
      
      {/* Variable Picker */}
      {showPicker && (
        <VariablePicker
          variables={getFilteredVariables()}
          position={pickerPosition}
          searchTerm={searchTerm}
          onSelect={insertVariable}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
};

export default RichTextInput;